import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Brain, Users, CalendarDays, Plane, LayoutGrid, ShieldCheck,
  Zap, RefreshCw, BookOpen, Scale, FileText, MapPin, Shield,
  Smartphone, DollarSign, Wrench, TrendingUp, Activity,
  Home, X, ArrowUpRight, Layers, Building, Link, Share2, Check,
} from "lucide-react";

// ─── Canvas ────────────────────────────────────────────────────────────────────
const CW = 3200;          // total canvas width (wider columns for bigger nodes)
const CH = 820;           // canvas height
const R  = 44;            // circle radius (88px diameter — presentation-visible)
const HEADER_H = 100;     // column-header band height
// x-centre of each column (10 columns, 320px apart)
const COL_X = [160, 480, 800, 1120, 1440, 1760, 2080, 2400, 2720, 3040];

// ─── Column metadata ───────────────────────────────────────────────────────────
const TIERS = [
  { id:0, label:"FOUNDATION",         color:"#3b82f6" },
  { id:1, label:"SCHEDULING",         color:"#6366f1" },
  { id:2, label:"CREW OPS",           color:"#06b6d4" },
  { id:3, label:"INTELLIGENCE",       color:"#f59e0b" },
  { id:4, label:"SAFETY CORE",        color:"#22c55e" },
  { id:5, label:"FRMS ADV.",          color:"#10b981" },
  { id:6, label:"GROUND OPS",         color:"#f97316" },
  { id:7, label:"GROUND INTEL",       color:"#ef4444" },
  { id:8, label:"PLATFORM",           color:"#a855f7" },
  { id:9, label:"ROADMAP",            color:"#f59e0b" },
];

// ─── Nodes ─────────────────────────────────────────────────────────────────────
const NODES = [
  // ── Col 0  Foundation ─────────────────────────────────────────────────────
  {
    id:"crew-master",   col:0, x:COL_X[0], y:280,
    label:"Crew Master", fullName:"Crew Master Database",
    status:"live", color:"#3b82f6", Icon:Users,
    stat:"275", statLabel:"Crew Profiles",
    headline:"Every crew member. One source of truth.",
    desc:"275 crew profiles across DEL/BOM/BLR/HYD/MAA/CCU/PNQ. Qualifications, medicals, leave, fatigue history — every scheduling and compliance decision originates here.",
    link:"/roster/crew",
    metrics:[
      { label:"Crew Profiles",    value:"275" },
      { label:"Active Bases",     value:"7 (incl. PNQ)" },
      { label:"Type Ratings",     value:"B737 / A320 / B777 / ATR" },
    ],
  },
  {
    id:"dgca-engine",   col:0, x:COL_X[0], y:540,
    label:"DGCA Engine", fullName:"DGCA CAR-OPS Rule Engine",
    status:"live", color:"#3b82f6", Icon:ShieldCheck,
    stat:"12", statLabel:"CAR-OPS Nov 2025 Rules",
    headline:"All 12 DGCA rules enforced before the roster is published.",
    desc:"FDP_DAY_MAX_13H, FDP_NIGHT_MAX_10H, REST_MIN_12H, WEEKLY_REST_168H, NIGHT_CONSEC_2, WEEKLY_FLIGHT_35H, MONTHLY_BLOCK_100H, ANNUAL_FLIGHT_1000H, ANNUAL_DUTY_1800H, QUAL_MISMATCH, MEDICAL_VALID, SPLIT_DUTY_NO_EXT.",
    link:"/roster/compliance",
    metrics:[
      { label:"Day FDP Cap",     value:"13h" },
      { label:"WOCL FDP Cap",    value:"10h (Nov 2025)" },
      { label:"Weekly Rest",     value:"48h in 168h" },
    ],
  },

  // ── Col 1  Scheduling ─────────────────────────────────────────────────────
  {
    id:"roster",        col:1, x:COL_X[1], y:225,
    label:"Roster Planner", fullName:"30-Day Roster Planner",
    status:"live", color:"#6366f1", Icon:CalendarDays,
    stat:"0", statLabel:"Manual FDTL Checks",
    headline:"30-day rosters built around rest, not convenience.",
    desc:"Gantt view with FDTL violation overlay. Every assignment pre-checked against DGCA limits before publish. Hover tooltips, violation drill-down, PDF + CSV export. Zero manual compliance checks.",
    link:"/roster/planner",
    metrics:[
      { label:"Planning Horizon",         value:"30 days" },
      { label:"Pre-Publish Violations",   value:"Caught 100%" },
      { label:"Export",                   value:"PDF + CSV" },
    ],
  },
  {
    id:"pairing",       col:1, x:COL_X[1], y:415,
    label:"Pairing Builder", fullName:"Flight Pairing Builder",
    status:"live", color:"#6366f1", Icon:Plane,
    stat:"37", statLabel:"B737 Aircraft",
    headline:"Every FDP engineered to the hour.",
    desc:"4-tab interface: Gantt, Route Map (Leaflet), Fleet Settings, Scenarios. Schedule Builder with Phase 1 + Phase 2 sectors. 32 Indian airports. Haversine block-hour estimator at 650 km/h.",
    link:"/roster/pairings",
    metrics:[
      { label:"Active Fleet",   value:"37 B737 MAX 8" },
      { label:"Airports",       value:"32 Indian" },
      { label:"Block-Hour Calc",value:"Haversine @650km/h" },
    ],
  },
  {
    id:"monthly",       col:1, x:COL_X[1], y:600,
    label:"Monthly Scheduler", fullName:"Monthly Crew Scheduler",
    status:"live", color:"#6366f1", Icon:LayoutGrid,
    stat:"30-day", statLabel:"Conflict-Free",
    headline:"One month. Every conflict found before it goes live.",
    desc:"Full-month horizon: leave, training, simulator slots and peak demand all conflict-checked before publication. Configure peak periods, generate optimised schedules.",
    link:"/roster/monthly-scheduler",
    metrics:[
      { label:"Conflict Scope",   value:"Leave + Training + Demand" },
      { label:"States",           value:"Draft / Published" },
      { label:"Peak Periods",     value:"User-configured" },
    ],
  },

  // ── Col 2  Crew Ops ───────────────────────────────────────────────────────
  {
    id:"deployment",    col:2, x:COL_X[2], y:225,
    label:"Crew Deployment", fullName:"Crew Deployment Tracker",
    status:"live", color:"#06b6d4", Icon:MapPin,
    stat:"69", statLabel:"Seeded Deployments",
    headline:"Know where every crew member is — right now.",
    desc:"Real-time positioning: at base, in transit, on duty, available for cover. Assign Capt/FO/CC1–CC3 to daily schedules. Draft → Confirm with fatigue + FDTL compliance check.",
    link:"/roster/deployment",
    metrics:[
      { label:"Stations",     value:"DEL / BOM / BLR / HYD / MAA / CCU" },
      { label:"Roles",        value:"5 per aircraft" },
      { label:"Compliance",   value:"Fatigue + FDTL checked" },
    ],
  },
  {
    id:"irops",         col:2, x:COL_X[2], y:415,
    label:"IROPS Recovery", fullName:"IROPS Recovery Console",
    status:"live", color:"#06b6d4", Icon:RefreshCw,
    stat:"< 60s", statLabel:"Crew Reassignment",
    headline:"Disruption resolved without a single fatigue breach.",
    desc:"Flight cancelled? Aircraft swapped? Crew reassigned in under 60 seconds respecting rest periods, WOCL limits and qualifications. Phase 3 swap workflow with mini-Gantt context.",
    link:"/roster/recovery",
    metrics:[
      { label:"Resolution Time",  value:"< 60 seconds" },
      { label:"FDTL Re-check",    value:"Automatic on swap" },
      { label:"Phone Calls",      value:"0 required" },
    ],
  },
  {
    id:"simulator",     col:2, x:COL_X[2], y:600,
    label:"AI Simulator", fullName:"AI Scenario Simulator",
    status:"live", color:"#06b6d4", Icon:Zap,
    stat:"∞", statLabel:"Scenarios",
    headline:"Run the crisis before it runs you.",
    desc:"Claude 4 Sonnet models any disruption — late aircraft, crew sick-off, weather diversion — returning a fully compliant recovery plan with fatigue scores within 15 seconds.",
    link:"/roster/simulator",
    metrics:[
      { label:"AI Model",         value:"Claude 4 Sonnet" },
      { label:"Response",         value:"< 15 seconds" },
      { label:"Output",           value:"Recovery plan + fatigue scores" },
    ],
  },

  // ── Col 3  Intelligence ───────────────────────────────────────────────────
  {
    id:"training",      col:3, x:COL_X[3], y:190,
    label:"Training Engine", fullName:"Training & Recurrency Engine",
    status:"live", color:"#f59e0b", Icon:BookOpen,
    stat:"2,820", statLabel:"Records",
    headline:"Training scheduled around fatigue, not slots.",
    desc:"2,820 records across 12+ DGCA modules. Expiry Watch, Compliance Matrix, Scheduling Wizard. No crew enters a simulator after a WOCL night turn.",
    link:"/training-engine",
    metrics:[
      { label:"Total Records",    value:"2,820" },
      { label:"Post-WOCL Sim",    value:"0 allowed" },
      { label:"Reg Refs",         value:"DGCA + ICAO + BCAS" },
    ],
  },
  {
    id:"fairness",      col:3, x:COL_X[3], y:340,
    label:"Fairness Engine", fullName:"Roster Fairness Engine",
    status:"live", color:"#f59e0b", Icon:Scale,
    stat:"Equitable", statLabel:"Duty Distribution",
    headline:"No captain carries three times the night duties.",
    desc:"Equity scoring distributes night duties, long pairings and weekend shifts proportionally. Measurably reduces grievances and improves retention.",
    link:"/fairness-engine",
    metrics:[
      { label:"Night Duty Variance",  value:"< 10% target" },
      { label:"Weekend Spread",       value:"Proportional" },
      { label:"Retention Impact",     value:"Measurable" },
    ],
  },
  {
    id:"staffing",      col:3, x:COL_X[3], y:485,
    label:"Staffing Engine", fullName:"Crew Staffing Formula Engine",
    status:"live", color:"#f59e0b", Icon:Users,
    stat:"4-method", statLabel:"Binding Calc",
    headline:"Exactly how many crew you need. Not one more.",
    desc:"MAX(Flight-Hour, Sector-Based, Duty-Day, Regulatory Minimum) with BINDING badge. AI-derived DGCA parameters via Claude. 33 aircraft types. Save and compare 50 runs.",
    link:"/staffing-engine",
    metrics:[
      { label:"Methods",      value:"4 (MAX = binding)" },
      { label:"Aircraft",     value:"33 types" },
      { label:"Run History",  value:"50 saved" },
    ],
  },
  {
    id:"compliance",    col:3, x:COL_X[3], y:630,
    label:"Compliance Board", fullName:"DGCA Compliance Board",
    status:"live", color:"#f59e0b", Icon:ShieldCheck,
    stat:"0", statLabel:"Manual Audits",
    headline:"Zero manual checks. All 12 DGCA rules enforced.",
    desc:"12 DGCA CAR-OPS Nov 2025 rules enforced in real time. FDP limits, weekly rest, night-duty caps, monthly block hours — flagged before the inspector. One-click PDF export.",
    link:"/roster/compliance",
    metrics:[
      { label:"Rules",        value:"12 (Nov 2025)" },
      { label:"PDF Export",   value:"Yes" },
      { label:"Readiness",    value:"Always on" },
    ],
  },

  // ── Col 4  Safety Core ────────────────────────────────────────────────────
  {
    id:"frms",          col:4, x:COL_X[4], y:225,
    label:"FRMS Core", fullName:"Fatigue Risk Management System",
    status:"live", color:"#22c55e", Icon:Brain,
    stat:"72h", statLabel:"Prediction Horizon",
    headline:"Predict fatigue before the flight is assigned.",
    desc:"Biomathematical SAFTE-FAST scoring for 185+ crew, 72 hours ahead. Manager SFR Live Feed polls every 30 seconds. Knows who is safe before the roster is published.",
    link:"/frms",
    metrics:[
      { label:"Crew Tracked",     value:"185" },
      { label:"Bio-Model",        value:"SAFE v4.2" },
      { label:"Window",           value:"72 hours" },
    ],
  },
  {
    id:"safety-analytics", col:4, x:COL_X[4], y:415,
    label:"Safety Analytics", fullName:"Safety Management System Analytics",
    status:"live", color:"#22c55e", Icon:TrendingUp,
    stat:"SMS", statLabel:"8 Categories",
    headline:"Safety trends — not just incident counts.",
    desc:"Full SMS report management with 8 category types, trend analysis charts and DGCA SDMS integration for regulatory occurrence submissions.",
    link:"/safety-analytics",
    metrics:[
      { label:"Categories",   value:"8 SMS types" },
      { label:"SDMS",         value:"DGCA integrated" },
      { label:"Trends",       value:"Real-time charts" },
    ],
  },
  {
    id:"audit",         col:4, x:COL_X[4], y:600,
    label:"Audit Log", fullName:"Immutable Audit Log",
    status:"live", color:"#22c55e", Icon:FileText,
    stat:"100%", statLabel:"DGCA Traceable",
    headline:"Every decision. Every change. Permanent record.",
    desc:"Every scheduling action, compliance override and crew change logged immutably — timestamp, user, reason. DGCA inspection-ready at any moment.",
    link:"/roster/audit",
    metrics:[
      { label:"Immutability",  value:"Guaranteed" },
      { label:"Retention",     value:"Unlimited" },
      { label:"Export",        value:"One-click" },
    ],
  },

  // ── Col 5  FRMS Advanced ──────────────────────────────────────────────────
  {
    id:"frms-phase3",   col:5, x:COL_X[5], y:225,
    label:"FRMS Phase 3", fullName:"FRMS Phase 3 — Full DGCA Framework",
    status:"live", color:"#10b981", Icon:Shield,
    stat:"Phase 3", statLabel:"DGCA FRMS",
    headline:"Full DGCA FRMS framework — from policy to audit.",
    desc:"Policy Library (6 docs), Hazard Register (7 hazards), Occurrence Reporting, Internal + DGCA Audits, Scientific Evidence (SAFE v4.2). All three DGCA phases implemented.",
    link:"/frms",
    metrics:[
      { label:"DGCA Phases",  value:"1 + 2 + 3" },
      { label:"Policies",     value:"6 documents" },
      { label:"Hazards",      value:"7 registered" },
    ],
  },
  {
    id:"occurrence",    col:5, x:COL_X[5], y:415,
    label:"Occurrence Rpt.", fullName:"Confidential Occurrence Reporting",
    status:"live", color:"#10b981", Icon:FileText,
    stat:"Non-punitive", statLabel:"Just Culture",
    headline:"Crew report fatigue. Without fear of blame.",
    desc:"Confidential, non-punitive crew reporting with anonymous option. High-stress events auto-flagged to safety team instantly. DGCA SDMS submission integration.",
    link:"/frms",
    metrics:[
      { label:"Anonymous",    value:"Yes" },
      { label:"Auto-Flag",    value:"High-stress events" },
      { label:"Culture",      value:"Just Culture enforced" },
    ],
  },
  {
    id:"sfr",           col:5, x:COL_X[5], y:600,
    label:"SFR Rating", fullName:"Subjective Fatigue Rating (PWA)",
    status:"live", color:"#10b981", Icon:Activity,
    stat:"1–7", statLabel:"Samn-Perelli",
    headline:"Pilots rate fatigue from their phone after every sector.",
    desc:"PWA mobile view: Samn-Perelli 1–7 scale with stressor chips. Scores ≥5 instantly flagged. sfr_delta (−5 to +12 pts) integrated into fatigue score.",
    link:"/my-sectors",
    metrics:[
      { label:"Scale",        value:"Samn-Perelli 1–7" },
      { label:"Flag Trigger", value:"Score ≥ 5" },
      { label:"Impact",       value:"sfr_delta ±12 pts" },
    ],
  },

  // ── Col 6  Ground Ops ─────────────────────────────────────────────────────
  {
    id:"turnaround",    col:6, x:COL_X[6], y:225,
    label:"Turnaround Tower", fullName:"Turnaround Control Tower",
    status:"live", color:"#f97316", Icon:Layers,
    stat:"225", statLabel:"Task Types",
    headline:"Every turnaround. Every task. One command view.",
    desc:"Live flight board, task timelines, exception view — 15 demo flights × 225 tasks across 8 stations. Real-time ground operation milestone tracking per flight.",
    link:"/turnaround",
    metrics:[
      { label:"Flights",      value:"15 demo" },
      { label:"Tasks",        value:"15 types per TAT" },
      { label:"Target TAT",   value:"45 minutes" },
    ],
  },
  {
    id:"ramp-pwa",      col:6, x:COL_X[6], y:415,
    label:"Ramp PWA", fullName:"Ramp Mobile App (PWA)",
    status:"live", color:"#f97316", Icon:Smartphone,
    stat:"PWA", statLabel:"Add to Home Screen",
    headline:"Ground crew on phones. Zero app-store friction.",
    desc:"Role-based PWA for ground crew: My Tasks, real-time updates, incident reports with photo evidence, staff reassignment — all from a mobile browser. Works offline.",
    link:"/ramp",
    metrics:[
      { label:"Offline",      value:"Yes (service worker)" },
      { label:"Photo",        value:"Base64 capture" },
      { label:"Roles",        value:"Agent + Supervisor" },
    ],
  },
  {
    id:"station-perf",  col:6, x:COL_X[6], y:600,
    label:"Station Perf.", fullName:"Station Performance Dashboard",
    status:"live", color:"#f97316", Icon:TrendingUp,
    stat:"OTP", statLabel:"Per-Station",
    headline:"Station-by-station performance. No surprises.",
    desc:"On-time performance, turnaround adherence, delay causes and trend charts per station. Identify consistently underperforming stations before they escalate.",
    link:"/turnaround/stations",
    metrics:[
      { label:"Metrics",      value:"OTP, TAT, Delay Codes" },
      { label:"Stations",     value:"8" },
      { label:"Trends",       value:"Weekly + Monthly" },
    ],
  },

  // ── Col 7  Ground Intelligence ────────────────────────────────────────────
  {
    id:"ai-delay",      col:7, x:COL_X[7], y:225,
    label:"AI Delay Pred.", fullName:"AI Delay Prediction Engine",
    status:"live", color:"#ef4444", Icon:Zap,
    stat:"0–100", statLabel:"Confidence Score",
    headline:"Know the delay before it becomes a delay.",
    desc:"Claude 4 Sonnet analyses 15 ground tasks, vendor performance, weather risk and time-to-departure. Returns delay confidence, key risk factors and OCC recommended action.",
    link:"/turnaround",
    metrics:[
      { label:"AI Model",     value:"Claude 4 Sonnet" },
      { label:"Output",       value:"Confidence 0–100" },
      { label:"Stored in",    value:"ground_predictions DB" },
    ],
  },
  {
    id:"vendor-sla",    col:7, x:COL_X[7], y:415,
    label:"Vendor SLA", fullName:"Ground Vendor SLA Manager",
    status:"live", color:"#ef4444", Icon:Building,
    stat:"6", statLabel:"Vendors Tracked",
    headline:"Hold vendors accountable. Automatically.",
    desc:"Celebi, AIASL, dnata, SATS, HPCL, Airline Engineering — SLA scores computed live from task-completion data. Grade: Excellent / Good / At Risk / Poor.",
    link:"/turnaround/vendors",
    metrics:[
      { label:"Vendors",      value:"Celebi, AIASL, dnata, SATS+" },
      { label:"Grade",        value:"Live computed" },
      { label:"Issues",       value:"Expandable cards" },
    ],
  },
  {
    id:"ground-fatigue", col:7, x:COL_X[7], y:600,
    label:"Ground Fatigue", fullName:"Ground Fatigue Shield",
    status:"live", color:"#ef4444", Icon:Shield,
    stat:"All roles", statLabel:"Ground Staff",
    headline:"FRMS extended to every ground role.",
    desc:"Fatigue scores and violations for all ground staff — not just pilots. Shift history, violation bar charts, expandable staff cards, station coverage gap analysis.",
    link:"/turnaround/ground-staff",
    metrics:[
      { label:"Roles",        value:"All ground operations" },
      { label:"Tracking",     value:"Score + Violations" },
      { label:"Parity",       value:"Air + Ground" },
    ],
  },

  // ── Col 8  Platform ───────────────────────────────────────────────────────
  {
    id:"pricing",       col:8, x:COL_X[8], y:320,
    label:"Pricing Calc.", fullName:"Pricing Impact Calculator",
    status:"live", color:"#a855f7", Icon:DollarSign,
    stat:"5-page", statLabel:"B2B PDF Proposal",
    headline:"Show the CFO the number. Then close the deal.",
    desc:"Cost per Revenue Seat, fleet cost %, preset airline profiles (Akasa, IndiGo, Yeti, Air India), INR/USD toggle, 3-way compare, branded 5-page B2B PDF export.",
    link:"/pricing-calculator",
    metrics:[
      { label:"Profiles",     value:"4 airlines" },
      { label:"Compare",      value:"3 scenarios" },
      { label:"PDF",          value:"5-page branded" },
    ],
  },
  {
    id:"integrations",  col:8, x:COL_X[8], y:530,
    label:"Integrations", fullName:"Enterprise Integrations Hub",
    status:"live", color:"#a855f7", Icon:Link,
    stat:"4", statLabel:"Live Connectors",
    headline:"Connected to every system that matters.",
    desc:"AMOS/Ramco MRO, Jeppesen/AIMS Crew, ACARS Live FDP Feed, DGCA SDMS — live data preview, manual sync trigger, uptime monitoring and latency metrics.",
    link:"/integrations",
    metrics:[
      { label:"Connectors",   value:"4 active" },
      { label:"Sync",         value:"Manual trigger" },
      { label:"Uptime",       value:"Monitored" },
    ],
  },

  // ── Col 9  Roadmap ────────────────────────────────────────────────────────
  {
    id:"mro-api",       col:9, x:COL_X[9], y:190,
    label:"Real MRO API", fullName:"Production MRO API Connectors",
    status:"planned", color:"#f59e0b", Icon:Wrench,
    stat:"P1", statLabel:"Roadmap",
    headline:"Live MRO data. No more manual sync.",
    desc:"Production API connectors to Ramco Aviation, AMOS and OASES — replacing mock data with real-time maintenance records, ADs and live work orders.",
    link:null,
    metrics:[
      { label:"Systems",    value:"Ramco, AMOS, OASES" },
      { label:"State",      value:"Mock connectors live" },
      { label:"Priority",   value:"P1" },
    ],
  },
  {
    id:"amos-sync",     col:9, x:COL_X[9], y:340,
    label:"AMOS Sync", fullName:"AMOS / Ramco Bidirectional Sync",
    status:"planned", color:"#f59e0b", Icon:RefreshCw,
    stat:"P1", statLabel:"Roadmap",
    headline:"AOG events auto-trigger IROPS recovery.",
    desc:"Bidirectional data sync between Fatigue Shield and AMOS/Ramco — AOG events automatically trigger the IROPS Recovery workflow and notify the OCC.",
    link:null,
    metrics:[
      { label:"Type",       value:"Bidirectional" },
      { label:"AOG Trigger",value:"Planned" },
      { label:"Priority",   value:"P1" },
    ],
  },
  {
    id:"what-if",       col:9, x:COL_X[9], y:485,
    label:"What-If Scenarios", fullName:"What-If Fleet Growth Simulator",
    status:"planned", color:"#f59e0b", Icon:TrendingUp,
    stat:"12-mo", statLabel:"Fleet Projection",
    headline:"Model 12 months of fleet growth before you commit.",
    desc:"Crew requirements for fleet expansion (5→10→20 aircraft). Staffing impact, hiring timeline, cost delta and FDTL compliance modelled ahead of purchase decisions.",
    link:null,
    metrics:[
      { label:"Horizon",    value:"12 months" },
      { label:"Fleet",      value:"5–50 aircraft" },
      { label:"Priority",   value:"P2" },
    ],
  },
  {
    id:"ml-safety",     col:9, x:COL_X[9], y:635,
    label:"ML Safety Intel.", fullName:"ML Safety Pattern Discovery",
    status:"future", color:"#6b7280", Icon:Brain,
    stat:"ML", statLabel:"Pattern Discovery",
    headline:"Find risk patterns humans cannot see.",
    desc:"Machine learning clustering on SMS reports, SFR fatigue ratings and FRMS scores to surface hidden risk patterns across crew cohorts, routes and time-of-day windows.",
    link:null,
    metrics:[
      { label:"Method",   value:"ML clustering" },
      { label:"Sources",  value:"SMS + SFR + FRMS" },
      { label:"Priority", value:"P3 Future" },
    ],
  },
];

const NODE_MAP = {};
NODES.forEach(n => { NODE_MAP[n.id] = n; });

// ─── Edges ─────────────────────────────────────────────────────────────────────
const EDGES = [
  ["crew-master","roster"],["crew-master","pairing"],["crew-master","monthly"],
  ["dgca-engine","pairing"],["dgca-engine","compliance"],["dgca-engine","monthly"],
  ["roster","deployment"],["pairing","deployment"],["pairing","irops"],["monthly","deployment"],
  ["deployment","training"],["deployment","fairness"],
  ["irops","simulator"],["simulator","staffing"],
  ["training","frms"],["fairness","frms"],["staffing","frms"],
  ["compliance","audit"],["compliance","safety-analytics"],
  ["frms","frms-phase3"],["frms","sfr"],
  ["safety-analytics","occurrence"],["audit","frms-phase3"],
  ["frms-phase3","turnaround"],["occurrence","ramp-pwa"],["sfr","ramp-pwa"],
  ["turnaround","ai-delay"],["turnaround","station-perf"],
  ["ramp-pwa","vendor-sla"],["station-perf","ground-fatigue"],
  ["ai-delay","pricing"],["vendor-sla","pricing"],["ground-fatigue","integrations"],
  ["pricing","what-if"],["integrations","mro-api"],["integrations","amos-sync"],
  ["safety-analytics","ml-safety"],
];

const BADGE = {
  live:    { text:"#22c55e", label:"LIVE" },
  planned: { text:"#f59e0b", label:"PLANNED" },
  future:  { text:"#6b7280", label:"FUTURE" },
};

const LIVE_COUNT = NODES.filter(n => n.status === "live").length;

// ─── Component ─────────────────────────────────────────────────────────────────
export default function ProductTree() {
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const skipParam  = searchParams.get("skip") === "1";
  const nodeParam  = searchParams.get("node");

  const [intro,       setIntro]       = useState(!skipParam && !nodeParam);
  const [activeId,    setActiveId]    = useState(nodeParam && NODE_MAP[nodeParam] ? nodeParam : null);
  const [visibleCol,  setVisibleCol]  = useState(skipParam || nodeParam ? 10 : -1);
  const [copied,      setCopied]      = useState(false);

  const scrollRef = useRef(null);
  const drag      = useRef({ on: false, startX: 0, sl: 0 });

  // Compute direct connections + full ancestor/descendant chain
  const { connectedIds, ancestorIds, descendantIds, chainIds } = useMemo(() => {
    if (!activeId) return { connectedIds: new Set(), ancestorIds: new Set(), descendantIds: new Set(), chainIds: new Set() };

    // 1 — Direct connections (1 hop)
    const connected = new Set();
    EDGES.forEach(([fId, tId]) => {
      if (fId === activeId) connected.add(tId);
      if (tId === activeId) connected.add(fId);
    });

    // 2 — Full ancestors: BFS backwards through EDGES
    const ancestors = new Set();
    let frontier = [activeId];
    const seenBack = new Set([activeId]);
    while (frontier.length) {
      const next = [];
      frontier.forEach(curr => {
        EDGES.forEach(([fId, tId]) => {
          if (tId === curr && !seenBack.has(fId)) {
            seenBack.add(fId);
            if (!connected.has(fId)) ancestors.add(fId);
            next.push(fId);
          }
        });
      });
      frontier = next;
    }

    // 3 — Full descendants: BFS forwards through EDGES
    const descendants = new Set();
    frontier = [activeId];
    const seenFwd = new Set([activeId]);
    while (frontier.length) {
      const next = [];
      frontier.forEach(curr => {
        EDGES.forEach(([fId, tId]) => {
          if (fId === curr && !seenFwd.has(tId)) {
            seenFwd.add(tId);
            if (!connected.has(tId)) descendants.add(tId);
            next.push(tId);
          }
        });
      });
      frontier = next;
    }

    return {
      connectedIds: connected,
      ancestorIds:  ancestors,
      descendantIds: descendants,
      chainIds: new Set([...ancestors, ...descendants]),
    };
  }, [activeId]);

  // Reveal columns after intro dismissed
  useEffect(() => {
    if (intro) return;
    if (skipParam || nodeParam) { setVisibleCol(10); return; }
    setVisibleCol(-1);
    let c = -1;
    const iv = setInterval(() => {
      c++;
      setVisibleCol(c);
      if (c >= 9) clearInterval(iv);
    }, 130);
    return () => clearInterval(iv);
  }, [intro, skipParam, nodeParam]);

  // Drag-to-scroll
  const onMD = e => {
    drag.current = { on: true, startX: e.pageX - scrollRef.current.offsetLeft, sl: scrollRef.current.scrollLeft };
    scrollRef.current.style.cursor = "grabbing";
  };
  const onMM = e => {
    if (!drag.current.on) return;
    e.preventDefault();
    scrollRef.current.scrollLeft = drag.current.sl - (e.pageX - scrollRef.current.offsetLeft - drag.current.startX);
  };
  const onMU = () => { drag.current.on = false; if (scrollRef.current) scrollRef.current.style.cursor = "grab"; };

  const activeNode = activeId ? NODE_MAP[activeId] : null;

  const handleShare = () => {
    const url = `${window.location.origin}/product-tree?node=${activeNode.id}`;
    navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2200); });
  };

  return (
    <div
      data-testid="product-tree"
      className="fixed inset-0"
      style={{ background: "#080c10", fontFamily: "system-ui, -apple-system, sans-serif" }}
    >

      {/* ── Top bar ──────────────────────────────────────────────────────── */}
      <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between px-6"
        style={{ height:52, background:"rgba(8,12,16,0.98)", borderBottom:"1px solid rgba(255,255,255,0.12)", backdropFilter:"blur(12px)" }}>
        <button data-testid="tree-home-btn" onClick={() => navigate("/")}
          className="flex items-center gap-2 font-mono tracking-widest hover:text-white transition-colors" style={{ fontSize:12, color:"rgba(255,255,255,0.5)" }}>
          <Home size={13}/> HOME
        </button>
        <div className="flex items-center gap-4">
          <span className="font-mono tracking-[0.35em] uppercase font-bold" style={{ fontSize:12, color:"rgba(255,255,255,0.75)" }}>
            Fatigue Shield — Product Tree
          </span>
          <span className="font-mono px-3 py-1 rounded" style={{ fontSize:11, color:"#22c55e", background:"rgba(34,197,94,0.15)", border:"1px solid rgba(34,197,94,0.4)" }}>
            {LIVE_COUNT} LIVE MODULES
          </span>
        </div>
        <button data-testid="tree-exit-btn" onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 font-mono tracking-widest hover:text-white transition-colors" style={{ fontSize:12, color:"rgba(255,255,255,0.5)" }}>
          <X size={13}/> EXIT
        </button>
      </div>

      {/* ── Intro overlay ────────────────────────────────────────────────── */}
      <AnimatePresence>
        {intro && (
          <motion.div key="intro"
            className="absolute inset-0 z-40 flex items-center justify-center"
            style={{ background:"rgba(8,12,16,0.82)", backdropFilter:"blur(5px)" }}
            initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0, transition:{ duration:0.5 } }}
            onClick={() => setIntro(false)}
          >
            <motion.div
              className="px-10 py-10 rounded-2xl text-center"
              style={{ background:"#0e1420", border:"1px solid rgba(255,255,255,0.1)", maxWidth:480, boxShadow:"0 24px 80px rgba(0,0,0,0.7)" }}
              initial={{ scale:0.9, opacity:0, y:20 }} animate={{ scale:1, opacity:1, y:0 }}
              transition={{ delay:0.05, type:"spring", stiffness:280, damping:28 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="font-mono tracking-[0.4em] uppercase text-blue-400/50 mb-4" style={{ fontSize:10 }}>
                Fatigue Shield
              </div>
              <h1 className="font-black text-white mb-4 leading-tight" style={{ fontSize:"clamp(2rem,4vw,2.8rem)" }}>
                The Product Tree
              </h1>
              <p className="text-white/45 leading-relaxed mb-7" style={{ fontSize:13 }}>
                30 modules. Every dependency mapped.<br/>
                From crew data to DGCA compliance — scroll left to right to explore how each feature builds on the last.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setIntro(false)}
                  className="px-7 py-2.5 rounded-lg font-semibold text-white transition-all hover:opacity-80"
                  style={{ fontSize:13, background:"#3b82f6" }}
                >
                  Explore the Tree →
                </button>
              </div>
              <div className="mt-5 text-white/20 font-mono" style={{ fontSize:9 }}>
                Drag to scroll · Click any node to explore
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Tree scroll area ─────────────────────────────────────────────── */}
      <div
        ref={scrollRef}
        data-testid="tree-canvas"
        className="absolute left-0 right-0 bottom-0"
        style={{
          top:52,
          overflowX:"auto",
          overflowY:"hidden",
          cursor:"grab",
          scrollbarWidth:"none",
          msOverflowStyle:"none",
        }}
        onMouseDown={onMD}
        onMouseMove={onMM}
        onMouseUp={onMU}
        onMouseLeave={onMU}
      >
        {/* hide webkit scrollbar */}
        <style>{`.tree-scroll::-webkit-scrollbar{display:none}`}</style>

        {/* Canvas */}
        <div style={{ width:CW, height:CH, position:"relative", userSelect:"none" }}>

          {/* ── SVG layer: dividers + connections ──────────────────────── */}
          <svg
            style={{ position:"absolute", top:0, left:0, width:CW, height:CH, pointerEvents:"none" }}
            viewBox={`0 0 ${CW} ${CH}`}
          >
            <defs>
              <filter id="glow">
                <feGaussianBlur stdDeviation="5" result="blur"/>
                <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
            </defs>

            {/* Horizontal header separator */}
            <line x1="0" y1={HEADER_H} x2={CW} y2={HEADER_H} stroke="rgba(255,255,255,0.12)" strokeWidth="1"/>

            {/* Vertical column dividers */}
            {COL_X.slice(0,-1).map((cx, i) => {
              const dx = (cx + COL_X[i+1]) / 2;
              return <line key={i} x1={dx} y1={HEADER_H} x2={dx} y2={CH} stroke="rgba(255,255,255,0.07)" strokeWidth="1"/>;
            })}

            {/* Connection curves */}
            {EDGES.map(([fId, tId], idx) => {
              const f = NODE_MAP[fId], t = NODE_MAP[tId];
              if (!f || !t) return null;
              const isDirectEdge = activeId === fId || activeId === tId;
              const allChain = activeId
                ? new Set([activeId, ...connectedIds, ...chainIds])
                : null;
              const isChainEdge = activeId && !isDirectEdge && allChain.has(fId) && allChain.has(tId);
              const edgeDimmed  = activeId && !isDirectEdge && !isChainEdge;
              const isPlanned   = f.status !== "live" || t.status !== "live";
              const mx = (f.x + t.x) / 2;
              return (
                <path key={idx}
                  d={`M${f.x},${f.y} C${mx},${f.y} ${mx},${t.y} ${t.x},${t.y}`}
                  fill="none"
                  filter={isDirectEdge ? "url(#glow)" : undefined}
                  stroke={isDirectEdge ? t.color : isChainEdge ? t.color + "99" : isPlanned ? "rgba(245,158,11,0.6)" : t.color + "cc"}
                  strokeWidth={isDirectEdge ? 4 : isChainEdge ? 2 : 2.5}
                  strokeDasharray={isPlanned && !isDirectEdge && !isChainEdge ? "8 5" : undefined}
                  opacity={isDirectEdge ? 1 : isChainEdge ? 0.6 : edgeDimmed ? 0.05 : 0.88}
                  style={{ transition:"opacity 0.3s, stroke-width 0.3s" }}
                />
              );
            })}
          </svg>

          {/* ── Column headers ──────────────────────────────────────────── */}
          {TIERS.map(tier => (
            <motion.div key={tier.id}
              style={{
                position:"absolute",
                left: COL_X[tier.id] - 110,
                top:0, width:220, height:HEADER_H,
                display:"flex", alignItems:"center", justifyContent:"center",
              }}
              initial={{ opacity:0 }}
              animate={{ opacity: visibleCol >= tier.id ? 1 : 0 }}
              transition={{ duration:0.4 }}
            >
              <div className="text-center">
                <div className="font-mono font-black tracking-[0.2em] uppercase" style={{ fontSize:13, color: tier.color, letterSpacing:"0.18em" }}>
                  {tier.label}
                </div>
                <div className="mt-2 mx-auto" style={{ width:36, height:3, background: tier.color + "70", borderRadius:2 }}/>
              </div>
            </motion.div>
          ))}

          {/* ── Nodes ───────────────────────────────────────────────────── */}
          {NODES.map(node => {
            const isAct       = activeId === node.id;
            const isConnected = !isAct && connectedIds.has(node.id);
            const isChain     = !isAct && !isConnected && chainIds.has(node.id);
            const isDimmed    = activeId && !isAct && !isConnected && !isChain;
            const isVisible   = visibleCol >= node.col;
            const badge       = BADGE[node.status];
            const isLive      = node.status === "live";

            return (
              <motion.button
                key={node.id}
                data-testid={`tree-node-${node.id}`}
                style={{
                  position:"absolute",
                  left: node.x - 72,
                  top:  node.y - R,
                  width:144,
                  background:"transparent",
                  border:"none",
                  cursor:"pointer",
                  padding:0,
                  display:"flex",
                  flexDirection:"column",
                  alignItems:"center",
                  gap:8,
                }}
                initial={{ scale:0, opacity:0 }}
                animate={{
                  scale:   isVisible ? 1 : 0,
                  opacity: isVisible
                    ? isDimmed ? 0.08
                    : isChain  ? 0.65
                    : 1
                    : 0,
                }}
                transition={{ type:"spring", stiffness:280, damping:24 }}
                whileHover={{ scale:1.08, opacity:1 }}
                onClick={() => setActiveId(isAct ? null : node.id)}
              >
                {/* Circle */}
                <div style={{
                  width:  R * 2,
                  height: R * 2,
                  borderRadius:"50%",
                  border: isAct
                    ? `3px solid ${node.color}`
                    : isConnected
                    ? `3px solid ${node.color}`
                    : isChain
                    ? `2px solid ${node.color}88`
                    : `2.5px solid ${node.color + (isLive ? "ee" : "77")}`,
                  background: isAct
                    ? node.color
                    : isConnected
                    ? `radial-gradient(circle, ${node.color}55 0%, ${node.color}22 100%)`
                    : isChain
                    ? `radial-gradient(circle, ${node.color}25 0%, ${node.color}0a 100%)`
                    : isLive
                    ? `radial-gradient(circle, ${node.color}3a 0%, ${node.color}15 100%)`
                    : `radial-gradient(circle, ${node.color}15 0%, transparent 100%)`,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  boxShadow: isAct
                    ? `0 0 0 5px ${node.color}35, 0 0 36px ${node.color}70`
                    : isConnected
                    ? `0 0 0 4px ${node.color}30, 0 0 24px ${node.color}55`
                    : isLive
                    ? `0 0 16px ${node.color}35`
                    : `0 0 8px ${node.color}18`,
                  transition:"all 0.25s ease",
                  flexShrink:0,
                }}>
                  <node.Icon
                    size={26}
                    color={
                      isAct       ? "#fff"
                      : isConnected ? node.color
                      : isChain   ? node.color + "cc"
                      : isLive    ? node.color
                      : node.color + "99"
                    }
                  />
                </div>

                {/* Label */}
                <div style={{
                  fontSize:13, lineHeight:1.3, textAlign:"center",
                  color: isAct       ? "#fff"
                    : isConnected ? "rgba(255,255,255,0.97)"
                    : isChain     ? "rgba(255,255,255,0.72)"
                    : isLive      ? "rgba(255,255,255,0.90)"
                    : "rgba(255,255,255,0.55)",
                  maxWidth:136, wordBreak:"break-word",
                  fontWeight: isAct || isConnected ? 700 : 600,
                  letterSpacing:"0.01em",
                }}>
                  {node.label}
                </div>

                {/* Status badge */}
                <div style={{ fontSize:9.5, letterSpacing:"0.2em", fontFamily:"monospace", fontWeight:700,
                  color: isDimmed ? badge.text + "88" : badge.text }}>
                  {badge.label}
                </div>
              </motion.button>
            );
          })}

          {/* ── Scroll hint ─────────────────────────────────────────────── */}
          {!intro && (
            <motion.div
              style={{ position:"absolute", right:16, bottom:16 }}
              initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:2 }}
            >
              <div className="flex items-center gap-2 font-mono" style={{ fontSize:11, color:"rgba(255,255,255,0.35)" }}>
                ← drag to scroll →
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* ── Detail panel ─────────────────────────────────────────────────── */}
      <AnimatePresence>
        {activeNode && (
          <motion.div key={activeNode.id}
            data-testid="tree-detail-panel"
            className="absolute right-0 bottom-0 z-30 overflow-y-auto"
            style={{ top:52, width:420, background:"rgba(8,12,16,0.99)", borderLeft:`1px solid ${activeNode.color}25` }}
            initial={{ x:400, opacity:0 }}
            animate={{ x:0, opacity:1 }}
            exit={{ x:400, opacity:0 }}
            transition={{ type:"spring", stiffness:320, damping:32 }}
          >
            <div style={{ height:2, background:activeNode.color, opacity:0.8 }}/>

            <div className="px-7 py-7">
              <div className="font-mono tracking-[0.4em] uppercase mb-5" style={{ fontSize:9, color:BADGE[activeNode.status].text }}>
                {BADGE[activeNode.status].label} — Column {activeNode.col + 1} / 10
              </div>

              <div className="flex items-center gap-3 mb-5">
                <div style={{ width:44, height:44, borderRadius:8, flexShrink:0, background:activeNode.color+"18", border:`1px solid ${activeNode.color}28`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                  <activeNode.Icon size={21} color={activeNode.color}/>
                </div>
                <div>
                  <div className="uppercase tracking-widest text-white/25 font-mono" style={{ fontSize:9 }}>Fatigue Shield Module</div>
                  <div className="font-bold text-white leading-tight mt-0.5" style={{ fontSize:13 }}>{activeNode.fullName}</div>
                </div>
              </div>

              <motion.div key={activeNode.id+"-s"} initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.07 }}>
                <div className="font-black leading-none mb-1" style={{ fontSize:"clamp(2.4rem,4.5vw,4rem)", color:activeNode.color }}>{activeNode.stat}</div>
                <div className="uppercase tracking-widest text-white/25 font-mono mb-5" style={{ fontSize:9 }}>{activeNode.statLabel}</div>
              </motion.div>

              <motion.h3 key={activeNode.id+"-h"} initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.13 }}
                className="font-bold text-white leading-snug mb-3" style={{ fontSize:13 }}>
                {activeNode.headline}
              </motion.h3>

              <motion.p key={activeNode.id+"-d"} initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.18 }}
                className="text-white/42 leading-relaxed mb-5" style={{ fontSize:12 }}>
                {activeNode.desc}
              </motion.p>

              <motion.div key={activeNode.id+"-m"} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:0.23 }}
                className="mb-6 pt-4 space-y-2.5" style={{ borderTop:"1px solid rgba(255,255,255,0.06)" }}>
                {activeNode.metrics.map((m,i) => (
                  <div key={i} className="flex justify-between items-center">
                    <span className="font-mono text-white/30" style={{ fontSize:11 }}>{m.label}</span>
                    <span className="font-bold" style={{ fontSize:11, color:activeNode.color+"bb" }}>{m.value}</span>
                  </div>
                ))}
              </motion.div>

              {/* ── Dependency chain summary ────────────────────────────── */}
              <motion.div key={activeNode.id+"-chain"} initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.30 }}
                className="mb-6 rounded-lg overflow-hidden"
                style={{ border:"1px solid rgba(255,255,255,0.07)", background:"rgba(255,255,255,0.02)" }}
              >
                <div className="px-4 pt-3 pb-2">
                  <div className="font-mono uppercase tracking-widest text-white/28 mb-2" style={{ fontSize:8 }}>
                    Full Dependency Chain — {ancestorIds.size + connectedIds.size + descendantIds.size + 1} modules
                  </div>
                  {/* Ancestors */}
                  {ancestorIds.size > 0 && (
                    <div className="mb-2">
                      <div className="font-mono text-white/20 mb-1" style={{ fontSize:8 }}>UPSTREAM FOUNDATION ({ancestorIds.size})</div>
                      <div className="flex flex-wrap gap-1">
                        {[...ancestorIds].map(id => {
                          const n = NODE_MAP[id];
                          return n ? (
                            <button key={id} onClick={() => setActiveId(id)}
                              className="px-1.5 py-0.5 rounded font-mono transition-opacity hover:opacity-80"
                              style={{ fontSize:8, background: n.color+"1a", color: n.color+"aa", border:`1px solid ${n.color}33` }}>
                              {n.label}
                            </button>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                  {/* Direct neighbors */}
                  {connectedIds.size > 0 && (
                    <div className="mb-2">
                      <div className="font-mono text-white/20 mb-1" style={{ fontSize:8 }}>DIRECT CONNECTIONS ({connectedIds.size})</div>
                      <div className="flex flex-wrap gap-1">
                        {[...connectedIds].map(id => {
                          const n = NODE_MAP[id];
                          return n ? (
                            <button key={id} onClick={() => setActiveId(id)}
                              className="px-1.5 py-0.5 rounded font-mono transition-opacity hover:opacity-80"
                              style={{ fontSize:8, background: n.color+"28", color: n.color, border:`1px solid ${n.color}55` }}>
                              {n.label}
                            </button>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                  {/* Descendants */}
                  {descendantIds.size > 0 && (
                    <div>
                      <div className="font-mono text-white/20 mb-1" style={{ fontSize:8 }}>DOWNSTREAM ENABLEMENT ({descendantIds.size})</div>
                      <div className="flex flex-wrap gap-1">
                        {[...descendantIds].map(id => {
                          const n = NODE_MAP[id];
                          return n ? (
                            <button key={id} onClick={() => setActiveId(id)}
                              className="px-1.5 py-0.5 rounded font-mono transition-opacity hover:opacity-80"
                              style={{ fontSize:8, background: n.color+"1a", color: n.color+"aa", border:`1px solid ${n.color}33` }}>
                              {n.label}
                            </button>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>

              <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:0.28 }}>
                {activeNode.link ? (
                  <button data-testid="tree-open-module-btn"
                    onClick={() => navigate(activeNode.link)}
                    className="flex items-center gap-2 font-semibold uppercase tracking-widest px-4 py-2.5 rounded transition-opacity hover:opacity-75"
                    style={{ fontSize:10, border:`1px solid ${activeNode.color}40`, color:activeNode.color, background:activeNode.color+"12" }}>
                    Open Live Module <ArrowUpRight size={11}/>
                  </button>
                ) : (
                  <div className="font-mono uppercase tracking-widest inline-block px-4 py-2.5 rounded"
                    style={{ fontSize:10, color:"rgba(255,255,255,0.2)", border:"1px solid rgba(255,255,255,0.07)" }}>
                    On Roadmap — Coming Soon
                  </div>
                )}

                <button data-testid="tree-share-btn"
                  onClick={handleShare}
                  className="mt-3 flex items-center gap-2 font-mono uppercase tracking-widest px-4 py-2.5 rounded w-full justify-center transition-all"
                  style={{ fontSize:10, border:"1px solid rgba(255,255,255,0.1)", color:copied?"#22c55e":"rgba(255,255,255,0.35)", background:copied?"rgba(34,197,94,0.08)":"rgba(255,255,255,0.03)", transition:"all 0.25s ease" }}>
                  {copied ? <Check size={11}/> : <Share2 size={11}/>}
                  {copied ? "Link copied!" : "Share this module"}
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Legend ────────────────────────────────────────────────────────── */}
      {!intro && (
        <motion.div className="fixed bottom-4 z-20 pointer-events-none"
          style={{ left:"50%", transform:"translateX(-50%)" }}
          initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} transition={{ delay:2.5 }}>
        <div className="flex items-center gap-5 px-5 py-2.5 rounded"
            style={{ background:"rgba(8,12,16,0.97)", border:"1px solid rgba(255,255,255,0.12)", backdropFilter:"blur(8px)" }}>
            {[
              { s:"live",    c:"#22c55e", l:"Live Module" },
              { s:"planned", c:"#f59e0b", l:"Planned" },
              { s:"future",  c:"#6b7280", l:"Future" },
            ].map(x => (
              <div key={x.s} className="flex items-center gap-2">
                <div style={{ width:9, height:9, borderRadius:"50%", background:x.c, border:`2px solid ${x.c}` }}/>
                <span className="font-mono font-semibold" style={{ fontSize:11, color: x.s==="live" ? "rgba(255,255,255,0.7)" : "rgba(255,255,255,0.45)" }}>
                  {x.l}
                </span>
              </div>
            ))}
            <div style={{ width:1, height:16, background:"rgba(255,255,255,0.1)" }}/>
            <div className="flex items-center gap-2">
              <svg width="26" height="10"><path d="M0 5 C13 5 13 5 26 5" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2.5"/></svg>
              <span className="font-mono font-semibold" style={{ fontSize:11, color:"rgba(255,255,255,0.5)" }}>Dependency</span>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
